<div class="skills-page-container">
  @if (initialLoadFailed() && loadError(); as error) {
    <tf-settings-load-error
      [title]="error"
      [message]="'SETTINGS.LOAD_ERROR.MESSAGE' | translate"
      [details]="loadErrorDetails()"
      [retrying]="isLoading()"
      (retry)="reloadSkills()" />
  } @else if (!hasLoadedOnce()) {
    <tf-settings-loading-placeholder [rows]="5" [columns]="4" />
  } @else if (showEmptyState()) {
    <tf-settings-inline-message
      [message]="actionError()"
      messageId="skills-action-error"
      (dismissed)="clearActionError()" />

    <tf-empty-state
      [title]="'SETTINGS_SKILLS.EMPTY.TITLE' | translate"
      [body]="'SETTINGS_SKILLS.EMPTY.BODY' | translate">
      <svg
        empty-state-illustration
        class="settings-empty-illustration skills-empty-illustration"
        width="96"
        height="96"
        viewBox="0 0 100 100"
        fill="none"
        aria-hidden="true">
        <path class="skills-empty-illustration__card-shadow" d="M34 19H74V74L54 65L34 74Z" />
        <path class="skills-empty-illustration__card" d="M28 16H72V78L50 68L28 78Z" />
        <path class="skills-empty-illustration__line" d="M38 32H62M38 43H54M38 54H60" />
      </svg>
      <p-button
        empty-state-actions
        icon="pi pi-plus"
        [label]="'SETTINGS_SKILLS.CREATE_SKILL' | translate"
        [disabled]="!canCreateSkill()"
        [pTooltip]="canCreateSkill() ? '' : ('GENERAL.NO_PERMISSION' | translate)"
        tooltipPosition="top"
        (onClick)="createSkill()" />
      <p-button
        empty-state-actions
        icon="pi pi-refresh"
        severity="secondary"
        [loading]="isLoading()"
        [disabled]="isLoading()"
        [ariaLabel]="'SETTINGS_SKILLS.RELOAD_SKILLS' | translate"
        (onClick)="reloadSkills()" />
    </tf-empty-state>
  } @else {
    <tf-settings-list-header
      [searchQuery]="searchQuery()"
      [searchPlaceholder]="'SETTINGS_SKILLS.SEARCH_PLACEHOLDER' | translate"
      [loading]="isLoading()"
      [refreshTooltip]="'SETTINGS_SKILLS.RELOAD_SKILLS' | translate"
      (searchChange)="onSearchChange($event)"
      (refresh)="reloadSkills()">
      <p-button
        headerActions
        [label]="'SETTINGS_SKILLS.CREATE_SKILL' | translate"
        icon="pi pi-plus"
        severity="primary"
        [disabled]="!canCreateSkill()"
        [pTooltip]="canCreateSkill() ? '' : ('GENERAL.NO_PERMISSION' | translate)"
        tooltipPosition="top"
        (onClick)="createSkill()" />
    </tf-settings-list-header>
    <tf-settings-inline-message
      [message]="actionError()"
      messageId="skills-action-error"
      (dismissed)="clearActionError()" />

    <tf-data-table
      [columns]="columns"
      [value]="editableSkills()"
      [loading]="false"
      [paginator]="true"
      [rows]="pageSize"
      [lazy]="true"
      [totalRecords]="totalRecords()"
      [currentPageReportTemplate]="'SETTINGS_SKILLS.PAGINATION_MESSAGE' | translate"
      styleClass="p-datatable-sm"
      emptyMessageKey="SETTINGS_SKILLS.NO_SKILLS_FOUND"
      (sorted)="onSort($event)"
      (paged)="onPageChange($event)">
      <ng-template tfColumnDef="_actions" let-skill>
        <p-button
          icon="pi pi-pencil"
          severity="secondary"
          [text]="true"
          [rounded]="true"
          size="small"
          [disabled]="!canUpdateSkill()"
          [pTooltip]="
            canUpdateSkill() ? ('GENERAL.EDIT' | translate) : ('GENERAL.NO_PERMISSION' | translate)
          "
          tooltipPosition="top"
          class="skills-action-button"
          (onClick)="editSkill(skill)" />
        <p-button
          icon="pi pi-trash"
          severity="danger"
          [text]="true"
          [rounded]="true"
          size="small"
          [disabled]="!canDeleteSkill()"
          [pTooltip]="
            canDeleteSkill()
              ? ('GENERAL.DELETE' | translate)
              : ('GENERAL.NO_PERMISSION' | translate)
          "
          tooltipPosition="top"
          (onClick)="deleteSkill(skill)" />
      </ng-template>
    </tf-data-table>
  }

  <!-- Edit Skill Dialog -->
  <p-dialog
    [visible]="isEditDialogVisible()"
    (visibleChange)="isEditDialogVisible.set($event)"
    [modal]="true"
    [style]="{ width: '37.5rem' }"
    header="{{ 'SETTINGS_SKILLS.EDIT_SKILL' | translate }}"
    [draggable]="false"
    [resizable]="false">
    <form [formGroup]="editSkillForm" class="skill-form">
      <div class="form-field">
        <label for="editSkill">
          {{ 'SETTINGS_SKILLS.SKILL' | translate }}
          <span class="required">*</span>
        </label>
        <input
          pInputText
          id="editSkill"
          type="text"
          formControlName="skill"
          [placeholder]="'SETTINGS_SKILLS.SKILL_PLACEHOLDER' | translate"
          class="w-full"
          [class.ng-invalid]="!!editServerErrors.value()['skill']"
          [attr.aria-invalid]="editServerErrors.value()['skill'] ? true : null"
          [attr.aria-describedby]="editServerErrors.value()['skill'] ? 'editSkill-error' : null" />
        @if (editSkillForm.get('skill')?.invalid && editSkillForm.get('skill')?.touched) {
          <small class="p-error">{{ 'SETTINGS_SKILLS.SKILL_REQUIRED' | translate }}</small>
        }
        @if (editServerErrors.value()['skill']; as error) {
          <small class="p-error" id="editSkill-error" role="alert">{{ error }}</small>
        }
      </div>

      <div class="form-field">
        <label for="editSkillType">
          {{ 'SETTINGS_SKILLS.SKILL_TYPE' | translate }}
          <span class="required">*</span>
        </label>
        <input
          pInputText
          id="editSkillType"
          type="text"
          formControlName="skillType"
          [placeholder]="'SETTINGS_SKILLS.SKILL_TYPE_PLACEHOLDER' | translate"
          class="w-full"
          [class.ng-invalid]="!!editServerErrors.value()['skillType']"
          [attr.aria-invalid]="editServerErrors.value()['skillType'] ? true : null"
          [attr.aria-describedby]="
            editServerErrors.value()['skillType'] ? 'editSkillType-error' : null
          " />
        @if (editSkillForm.get('skillType')?.invalid && editSkillForm.get('skillType')?.touched) {
          <small class="p-error">{{ 'SETTINGS_SKILLS.SKILL_TYPE_REQUIRED' | translate }}</small>
        }
        @if (editServerErrors.value()['skillType']; as error) {
          <small class="p-error" id="editSkillType-error" role="alert">{{ error }}</small>
        }
      </div>

      <div class="form-field">
        <label for="editName">
          {{ 'SETTINGS_SKILLS.NAME' | translate }}
          <span class="required">*</span>
        </label>
        <input
          pInputText
          id="editName"
          type="text"
          formControlName="name"
          [placeholder]="'SETTINGS_SKILLS.NAME_PLACEHOLDER' | translate"
          class="w-full"
          [class.ng-invalid]="!!editServerErrors.value()['name']"
          [attr.aria-invalid]="editServerErrors.value()['name'] ? true : null"
          [attr.aria-describedby]="editServerErrors.value()['name'] ? 'editName-error' : null" />
        @if (editSkillForm.get('name')?.invalid && editSkillForm.get('name')?.touched) {
          <small class="p-error">{{ 'SETTINGS_SKILLS.NAME_REQUIRED' | translate }}</small>
        }
        @if (editServerErrors.value()['name']; as error) {
          <small class="p-error" id="editName-error" role="alert">{{ error }}</small>
        }
      </div>

      <div class="form-field">
        <label for="editShortKey">
          {{ 'SETTINGS_SKILLS.SHORT_KEY' | translate }}
          <span class="required">*</span>
        </label>
        <input
          pInputText
          id="editShortKey"
          type="text"
          formControlName="shortKey"
          [placeholder]="'SETTINGS_SKILLS.SHORT_KEY_PLACEHOLDER' | translate"
          class="w-full"
          [class.ng-invalid]="!!editServerErrors.value()['shortKey']"
          [attr.aria-invalid]="editServerErrors.value()['shortKey'] ? true : null"
          [attr.aria-describedby]="
            editServerErrors.value()['shortKey'] ? 'editShortKey-error' : null
          " />
        @if (editSkillForm.get('shortKey')?.invalid && editSkillForm.get('shortKey')?.touched) {
          <small class="p-error">{{ 'SETTINGS_SKILLS.SHORT_KEY_REQUIRED' | translate }}</small>
        }
        @if (editServerErrors.value()['shortKey']; as error) {
          <small class="p-error" id="editShortKey-error" role="alert">{{ error }}</small>
        }
      </div>
    </form>

    <ng-template #footer>
      <p-button
        label="{{ 'GENERAL.CANCEL' | translate }}"
        severity="secondary"
        [outlined]="true"
        (onClick)="closeEditDialog()" />
      <p-button
        label="{{ 'GENERAL.SAVE' | translate }}"
        severity="primary"
        [disabled]="editSkillForm.invalid || editSkillForm.pristine"
        (onClick)="saveEditing()" />
    </ng-template>
  </p-dialog>

  <!-- Create Skill Dialog -->
  <p-dialog
    [visible]="isCreateDialogVisible()"
    (visibleChange)="isCreateDialogVisible.set($event)"
    [modal]="true"
    [style]="{ width: '37.5rem' }"
    header="{{ 'SETTINGS_SKILLS.CREATE_SKILL' | translate }}"
    [draggable]="false"
    [resizable]="false">
    <form [formGroup]="createSkillForm" class="skill-form">
      <div class="form-field">
        <label for="newSkill">
          {{ 'SETTINGS_SKILLS.SKILL' | translate }}
          <span class="required">*</span>
        </label>
        <input
          pInputText
          id="newSkill"
          type="text"
          formControlName="skill"
          [placeholder]="'SETTINGS_SKILLS.SKILL_PLACEHOLDER' | translate"
          class="w-full"
          [class.ng-invalid]="!!createServerErrors.value()['skill']"
          [attr.aria-invalid]="createServerErrors.value()['skill'] ? true : null"
          [attr.aria-describedby]="createServerErrors.value()['skill'] ? 'newSkill-error' : null" />
        @if (createSkillForm.get('skill')?.invalid && createSkillForm.get('skill')?.touched) {
          <small class="p-error">{{ 'SETTINGS_SKILLS.SKILL_REQUIRED' | translate }}</small>
        }
        @if (createServerErrors.value()['skill']; as error) {
          <small class="p-error" id="newSkill-error" role="alert">{{ error }}</small>
        }
      </div>

      <div class="form-field">
        <label for="newSkillType">
          {{ 'SETTINGS_SKILLS.SKILL_TYPE' | translate }}
          <span class="required">*</span>
        </label>
        <input
          pInputText
          id="newSkillType"
          type="text"
          formControlName="skillType"
          [placeholder]="'SETTINGS_SKILLS.SKILL_TYPE_PLACEHOLDER' | translate"
          class="w-full"
          [class.ng-invalid]="!!createServerErrors.value()['skillType']"
          [attr.aria-invalid]="createServerErrors.value()['skillType'] ? true : null"
          [attr.aria-describedby]="
            createServerErrors.value()['skillType'] ? 'newSkillType-error' : null
          " />
        @if (
          createSkillForm.get('skillType')?.invalid && createSkillForm.get('skillType')?.touched
        ) {
          <small class="p-error">{{ 'SETTINGS_SKILLS.SKILL_TYPE_REQUIRED' | translate }}</small>
        }
        @if (createServerErrors.value()['skillType']; as error) {
          <small class="p-error" id="newSkillType-error" role="alert">{{ error }}</small>
        }
      </div>

      <div class="form-field">
        <label for="newName">
          {{ 'SETTINGS_SKILLS.NAME' | translate }}
          <span class="required">*</span>
        </label>
        <input
          pInputText
          id="newName"
          type="text"
          formControlName="name"
          [placeholder]="'SETTINGS_SKILLS.NAME_PLACEHOLDER' | translate"
          class="w-full"
          [class.ng-invalid]="!!createServerErrors.value()['name']"
          [attr.aria-invalid]="createServerErrors.value()['name'] ? true : null"
          [attr.aria-describedby]="createServerErrors.value()['name'] ? 'newName-error' : null" />
        @if (createSkillForm.get('name')?.invalid && createSkillForm.get('name')?.touched) {
          <small class="p-error">{{ 'SETTINGS_SKILLS.NAME_REQUIRED' | translate }}</small>
        }
        @if (createServerErrors.value()['name']; as error) {
          <small class="p-error" id="newName-error" role="alert">{{ error }}</small>
        }
      </div>

      <div class="form-field">
        <label for="newShortKey">
          {{ 'SETTINGS_SKILLS.SHORT_KEY' | translate }}
          <span class="required">*</span>
        </label>
        <input
          pInputText
          id="newShortKey"
          type="text"
          formControlName="shortKey"
          [placeholder]="'SETTINGS_SKILLS.SHORT_KEY_PLACEHOLDER' | translate"
          class="w-full"
          [class.ng-invalid]="!!createServerErrors.value()['shortKey']"
          [attr.aria-invalid]="createServerErrors.value()['shortKey'] ? true : null"
          [attr.aria-describedby]="
            createServerErrors.value()['shortKey'] ? 'newShortKey-error' : null
          " />
        @if (createSkillForm.get('shortKey')?.invalid && createSkillForm.get('shortKey')?.touched) {
          <small class="p-error">{{ 'SETTINGS_SKILLS.SHORT_KEY_REQUIRED' | translate }}</small>
        }
        @if (createServerErrors.value()['shortKey']; as error) {
          <small class="p-error" id="newShortKey-error" role="alert">{{ error }}</small>
        }
      </div>
    </form>

    <ng-template #footer>
      <p-button
        label="{{ 'GENERAL.CANCEL' | translate }}"
        severity="secondary"
        [outlined]="true"
        (onClick)="closeCreateDialog()" />
      <p-button
        label="{{ 'SETTINGS_SKILLS.CREATE_SKILL' | translate }}"
        severity="primary"
        [disabled]="createSkillForm.invalid"
        (onClick)="saveNewSkill()" />
    </ng-template>
  </p-dialog>

  <p-confirmDialog />
</div>
